import { Link } from 'react-router-dom';
import { ArrowRight, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ACHIEVEMENTS_BY_LEVEL, categoriesForLevel, type SurfLevel } from '@/lib/surf-progression';

interface NextMilestoneCardProps {
  level: SurfLevel;
  completedIds: Set<string>;
}

/**
 * Closes the loop the dashboard is otherwise implicit about: what to work on
 * next, specifically. The milestone shown is derived entirely from real
 * achievement/category data -- the category the user is furthest behind in
 * (lowest completion ratio, ties broken by taxonomy order), and the first
 * incomplete achievement within it. No fabricated claim like "3 away from
 * rail-work mastery" -- there's no data to support a statement that
 * specific, so this stays to what the taxonomy can actually prove.
 */
const NextMilestoneCard = ({ level, completedIds }: NextMilestoneCardProps) => {
  const achievements = ACHIEVEMENTS_BY_LEVEL[level].filter((a) => !a.optional);
  const categories = categoriesForLevel(level).map((category) => {
    const inCategory = achievements.filter((a) => a.category === category);
    const done = inCategory.filter((a) => completedIds.has(a.id)).length;
    return { category, done, total: inCategory.length, ratio: inCategory.length > 0 ? done / inCategory.length : 1 };
  });

  const behindest = categories
    .filter((c) => c.done < c.total)
    .sort((a, b) => a.ratio - b.ratio)[0];

  if (!behindest) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card px-5 py-5 sm:px-7 sm:py-6 text-center">
        <Target className="h-5 w-5 text-primary mx-auto mb-2" />
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Your Next Milestone</p>
        <p className="mt-2 font-serif text-lg text-foreground">
          You've completed every {level} achievement.
        </p>
        <Button asChild variant="ghost" size="sm" className="mt-3 gap-1.5 hover:bg-transparent hover:text-primary">
          <Link to="/my-swell-breaks/progression">
            See what's next
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
    );
  }

  const milestoneAchievement = achievements.find(
    (a) => a.category === behindest.category && !completedIds.has(a.id)
  )!;
  const remainingInCategory = behindest.total - behindest.done;

  return (
    <div className="rounded-2xl border border-border/50 bg-card px-5 py-5 sm:px-7 sm:py-6">
      <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Target className="h-3.5 w-3.5" />
        Your Next Milestone
      </p>
      <h3 className="mt-1.5 font-serif text-xl sm:text-2xl text-foreground">{milestoneAchievement.name}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground">
        You're{' '}
        <span className="text-foreground font-medium">
          {remainingInCategory} achievement{remainingInCategory === 1 ? '' : 's'}
        </span>{' '}
        away from completing {behindest.category}.
      </p>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
        <Button asChild variant="ghost" size="sm" className="gap-1.5 px-0 hover:bg-transparent hover:text-primary">
          <Link to="/my-swell-breaks/progression">
            See how to achieve it
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
        <Button asChild variant="ghost" size="sm" className="gap-1.5 px-0 hover:bg-transparent hover:text-primary">
          <Link to="/trip-planner">
            Best places to work on it
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default NextMilestoneCard;
